import React from "react";
import { Link } from "react-router-dom";
import { TopItem } from "../../../types/llm-usage.types";
import { formatCurrency } from "../../../utils/formatters";

interface TopRankingsProps {
  topSources: TopItem[];
  topProjects: TopItem[];
  topOperators: TopItem[];
  topArticles: TopItem[];
}

interface RankingSectionProps {
  title: string;
  data: TopItem[];
  testId: string;
  showLinks?: boolean;
  isExternalLink?: boolean;
}

const RankingSection: React.FC<RankingSectionProps> = ({
  title,
  data,
  testId,
  showLinks = false,
  isExternalLink = false,
}) => {
  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>
        <div className="text-neutral-500 text-sm">Brak danych</div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-4"
      data-testid={testId}
    >
      <h4 className="text-sm font-semibold text-neutral-900 mb-3">{title}</h4>

      <div className="space-y-2">
        {data.map((item, index) => (
          <div
            key={item.id}
            className="flex items-center gap-3 py-2 border-b border-neutral-50 last:border-0"
            data-testid={`${testId}-item-${index}`}
          >
            {/* Rank badge */}
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                index === 0
                  ? "bg-yellow-100 text-yellow-700"
                  : index === 1
                    ? "bg-neutral-100 text-neutral-600"
                    : index === 2
                      ? "bg-amber-100 text-amber-700"
                      : "bg-neutral-50 text-neutral-500"
              }`}
            >
              {index + 1}
            </div>

            {/* Name */}
            <div className="flex-1 min-w-0">
              {showLinks && item.url ? (
                isExternalLink ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline truncate block"
                    title={item.name}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.url}
                    className="text-sm text-primary-600 hover:text-primary-700 hover:underline truncate block"
                    title={item.name}
                  >
                    {item.name}
                  </Link>
                )
              ) : (
                <div
                  className="text-sm text-neutral-700 truncate"
                  title={item.name}
                >
                  {item.name}
                </div>
              )}
              <div className="text-xs text-neutral-400">
                {item.count.toLocaleString()} rekordów
              </div>
            </div>

            {/* Cost */}
            <div className="text-sm font-medium text-neutral-900 whitespace-nowrap">
              {formatCurrency(item.totalCostUsd)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TopRankings: React.FC<TopRankingsProps> = ({
  topSources,
  topProjects,
  topOperators,
  topArticles,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <RankingSection
        title="Top źródła"
        data={topSources}
        testId="ranking-sources"
        showLinks
      />
      <RankingSection
        title="Top projekty"
        data={topProjects}
        testId="ranking-projects"
        showLinks
      />
      <RankingSection
        title="Top operatorzy"
        data={topOperators}
        testId="ranking-operators"
        showLinks
      />
      <RankingSection
        title="Top artykuły"
        data={topArticles}
        testId="ranking-articles"
        showLinks
        isExternalLink
      />
    </div>
  );
};

export default TopRankings;
